import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import CommissionCalculator from '../components/CommissionCalculator';
import { 
  Target, 
  Calendar, 
  TrendingUp, 
  DollarSign, 
  Users,
  ArrowUpRight,
  ArrowDownRight,
  Activity,
  Bell,
  Clock,
  Building2,
  CheckCircle2,
  ExternalLink
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const COLORS = ['#1B365D', '#0071E3', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#64748B'];

// Qualified leads payment request panel for agents
const QualifiedLeadsPayment = ({ getAuthHeaders, navigate }) => {
  const [qualifiedLeads, setQualifiedLeads] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchQualified = async () => {
      try {
        const resp = await axios.get(`${API_URL}/api/leads/qualified-for-payment`, getAuthHeaders());
        setQualifiedLeads(resp.data);
      } catch (e) {
        console.error('Failed to fetch qualified leads:', e);
      } finally {
        setLoading(false);
      }
    };
    fetchQualified();
  }, [getAuthHeaders]);

  const handleRequestPayment = async (leadId) => {
    try {
      await axios.post(`${API_URL}/api/leads/${leadId}/request-payment`, {}, getAuthHeaders());
      setQualifiedLeads(prev => prev.filter(l => l.id !== leadId));
      // toast imported by parent via sonner
      window.dispatchEvent(new CustomEvent('toast', { detail: { message: 'Payment request submitted!' } }));
    } catch (error) {
      console.error('Payment request failed:', error);
    }
  };

  if (loading || qualifiedLeads.length === 0) return null;

  return (
    <Card className="border-0 shadow-apple border-l-4 border-l-emerald-500" data-testid="qualified-leads-payment">
      <CardHeader className="pb-2">
        <CardTitle className="font-heading text-lg text-[#1B365D] flex items-center gap-2">
          <DollarSign className="w-5 h-5 text-emerald-600" />
          Ready for Payment ({qualifiedLeads.length})
        </CardTitle>
        <CardDescription>These leads have been qualified by admin. You can request payment.</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {qualifiedLeads.map(lead => (
            <div key={lead.id} className="flex items-center justify-between p-3 rounded-xl bg-emerald-50/50 border border-emerald-100">
              <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate(`/leads/${lead.id}`)}>
                <div className="w-9 h-9 rounded-full bg-emerald-100 flex items-center justify-center">
                  <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="font-medium text-slate-900 text-sm">{lead.organization_name}</p>
                  <p className="text-xs text-slate-500">{lead.contact_name}</p>
                </div>
              </div>
              <Button
                size="sm"
                className="bg-emerald-600 hover:bg-emerald-700 rounded-full text-white"
                onClick={() => handleRequestPayment(lead.id)}
                data-testid={`request-payment-${lead.id}`}
              >
                <DollarSign className="w-3 h-3 mr-1" />
                Request Payment
              </Button>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

const Dashboard = () => {
  const { getAuthHeaders, isAdmin, user } = useAuth();
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState(null);
  const [followUps, setFollowUps] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnalytics();
    fetchFollowUps();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/analytics/dashboard`, getAuthHeaders());
      setAnalytics(response.data);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchFollowUps = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/leads/follow-ups`, getAuthHeaders());
      setFollowUps(response.data);
    } catch (error) {
      console.error('Failed to fetch follow-ups:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-slate-500">Loading dashboard...</div>
      </div>
    );
  }

  const statusData = analytics?.leads_by_status 
    ? Object.entries(analytics.leads_by_status).map(([name, value]) => ({ name, value }))
    : [];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="space-y-8"
      data-testid="dashboard"
    >
      {/* Header */}
      <div>
        <h1 className="font-heading text-3xl md:text-4xl font-bold text-[#1B365D] tracking-tight">
          Welcome back, {user?.name?.split(' ')[0]}
        </h1>
        <p className="mt-2 text-slate-500">
          Here's what's happening with your referrals today.
        </p>
      </div>

      {/* KPI Cards */}
      <motion.div 
        variants={itemVariants}
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
      >
        <Card className="border-0 shadow-apple bg-white hover:shadow-lg transition-shadow duration-300">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">Total Leads</p>
                <p className="mt-2 text-3xl font-bold text-[#1B365D]">{analytics?.total_leads || 0}</p>
              </div>
              <div className="p-3 bg-[#1B365D]/10 rounded-xl">
                <Target className="w-6 h-6 text-[#1B365D]" />
              </div>
            </div>
            <div className="mt-4 flex items-center gap-1 text-sm">
              <ArrowUpRight className="w-4 h-4 text-green-500" />
              <span className="text-green-500 font-medium">12%</span>
              <span className="text-slate-400">vs last month</span>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-apple bg-white hover:shadow-lg transition-shadow duration-300">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">Demo Requests</p>
                <p className="mt-2 text-3xl font-bold text-[#0071E3]">{analytics?.demo_requests || 0}</p>
              </div>
              <div className="p-3 bg-[#0071E3]/10 rounded-xl">
                <Calendar className="w-6 h-6 text-[#0071E3]" />
              </div>
            </div>
            <div className="mt-4 flex items-center gap-1 text-sm">
              <ArrowUpRight className="w-4 h-4 text-green-500" />
              <span className="text-green-500 font-medium">8%</span>
              <span className="text-slate-400">vs last month</span>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-apple bg-white hover:shadow-lg transition-shadow duration-300">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">Conversion Rate</p>
                <p className="mt-2 text-3xl font-bold text-emerald-600">{analytics?.conversion_rate || 0}%</p>
              </div>
              <div className="p-3 bg-emerald-100 rounded-xl">
                <TrendingUp className="w-6 h-6 text-emerald-600" />
              </div>
            </div>
            <div className="mt-4 flex items-center gap-1 text-sm">
              <ArrowUpRight className="w-4 h-4 text-green-500" />
              <span className="text-green-500 font-medium">2.5%</span>
              <span className="text-slate-400">vs last month</span>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-apple bg-white hover:shadow-lg transition-shadow duration-300">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">
                  {isAdmin ? 'Total Commission Paid' : 'Commission Earned'}
                </p>
                <p className="mt-2 text-3xl font-bold text-emerald-600">
                  ${(analytics?.commission_earned || 0).toLocaleString()}
                </p>
              </div>
              <div className="p-3 bg-emerald-100 rounded-xl">
                <DollarSign className="w-6 h-6 text-emerald-600" />
              </div>
            </div>
            <div className="mt-4 flex items-center gap-2 text-sm text-slate-500">
              <span>{analytics?.qualified_leads || 0} qualified</span>
              <span>•</span>
              <span>{analytics?.closed_deals || 0} closed</span>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Commission Calculator (for Agents) - NEW COMPONENT */}
      {!isAdmin && (
        <motion.div variants={itemVariants}>
          <CommissionCalculator />
        </motion.div>
      )}

      {/* Qualified Leads - Request Payment (for Agents) */}
      {!isAdmin && (
        <QualifiedLeadsPayment getAuthHeaders={getAuthHeaders} navigate={navigate} />
      )}

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Lead Trend Chart */}
        <motion.div variants={itemVariants}>
          <Card className="border-0 shadow-apple">
            <CardHeader>
              <CardTitle className="font-heading text-lg text-[#1B365D]">Lead Trend</CardTitle>
              <CardDescription>Monthly lead acquisition over 6 months</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={analytics?.monthly_trend || []}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis dataKey="month" stroke="#64748B" fontSize={12} />
                    <YAxis stroke="#64748B" fontSize={12} />
                    <Tooltip 
                      contentStyle={{ 
                        borderRadius: '12px', 
                        border: 'none', 
                        boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' 
                      }} 
                    />
                    <Line 
                      type="monotone" 
                      dataKey="leads" 
                      stroke="#0071E3" 
                      strokeWidth={3}
                      dot={{ fill: '#0071E3', strokeWidth: 2 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Pipeline Status */}
        <motion.div variants={itemVariants}>
          <Card className="border-0 shadow-apple">
            <CardHeader>
              <CardTitle className="font-heading text-lg text-[#1B365D]">Pipeline Status</CardTitle>
              <CardDescription>Distribution of leads by stage</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={statusData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={90}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {statusData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip 
                      contentStyle={{ 
                        borderRadius: '12px', 
                        border: 'none', 
                        boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' 
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="flex flex-wrap gap-2 mt-4 justify-center">
                {statusData.map((entry, index) => (
                  <div key={entry.name} className="flex items-center gap-2 text-xs">
                    <div 
                      className="w-3 h-3 rounded-full" 
                      style={{ backgroundColor: COLORS[index % COLORS.length] }}
                    />
                    <span className="text-slate-600 capitalize">{entry.name.replace('_', ' ')}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Bottom Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Activity */}
        <motion.div variants={itemVariants} className="lg:col-span-2">
          <Card className="border-0 shadow-apple">
            <CardHeader>
              <CardTitle className="font-heading text-lg text-[#1B365D] flex items-center gap-2">
                <Activity className="w-5 h-5" />
                Recent Activity
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {analytics?.recent_activities?.length > 0 ? (
                  analytics.recent_activities.slice(0, 5).map((activity, index) => (
                    <div 
                      key={activity.id || index} 
                      className="flex items-start gap-4 p-3 rounded-xl hover:bg-slate-50 transition-colors"
                    >
                      <div className="w-10 h-10 rounded-full bg-[#1B365D]/10 flex items-center justify-center flex-shrink-0">
                        <Activity className="w-5 h-5 text-[#1B365D]" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-900">{activity.description}</p>
                        <p className="text-xs text-slate-500 mt-1">
                          by {activity.user_name} • {new Date(activity.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      <Badge variant="secondary" className="text-xs capitalize">
                        {activity.activity_type.replace('_', ' ')}
                      </Badge>
                    </div>
                  ))
                ) : (
                  <p className="text-center text-slate-500 py-8">No recent activity</p>
                )}
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Leaderboard (Admin only) OR Follow-up Reminders */}
        {isAdmin ? (
          <motion.div variants={itemVariants}>
            <Card className="border-0 shadow-apple">
              <CardHeader>
                <CardTitle className="font-heading text-lg text-[#1B365D] flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  Top Performers
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {analytics?.leaderboard?.length > 0 ? (
                    analytics.leaderboard.map((agent, index) => (
                      <div 
                        key={agent.name}
                        className="flex items-center gap-3 p-3 rounded-xl bg-slate-50"
                      >
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold text-white ${
                          index === 0 ? 'bg-amber-500' : index === 1 ? 'bg-slate-400' : 'bg-amber-700'
                        }`}>
                          {index + 1}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-900 truncate">{agent.name}</p>
                        </div>
                        <p className="text-sm font-semibold text-[#1B365D]">{agent.leads} leads</p>
                      </div>
                    ))
                  ) : (
                    <p className="text-center text-slate-500 py-4">No data yet</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </motion.div>
        ) : (
          <motion.div variants={itemVariants}>
            <Card className="border-0 shadow-apple">
              <CardHeader>
                <CardTitle className="font-heading text-lg text-[#1B365D] flex items-center gap-2">
                  <Bell className="w-5 h-5" />
                  Follow-up Reminders
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {followUps.length > 0 ? (
                    followUps.slice(0, 5).map((lead) => (
                      <div 
                        key={lead.id}
                        onClick={() => navigate(`/leads/${lead.id}`)}
                        className="flex items-center gap-3 p-3 rounded-xl bg-amber-50 border border-amber-100 cursor-pointer hover:bg-amber-100 transition-colors"
                      >
                        <div className="w-8 h-8 rounded-full bg-amber-200 flex items-center justify-center">
                          <Clock className="w-4 h-4 text-amber-700" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-900 truncate">{lead.organization_name}</p>
                          <p className="text-xs text-amber-600">Due: {new Date(lead.next_follow_up).toLocaleDateString()}</p>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-center text-slate-500 py-4">No pending follow-ups</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}
      </div>
    </motion.div>
  );
};

export default Dashboard;
